<script setup>
import axios from "axios";
import { computed, ref } from "vue";
import router, { checkTokens } from "@/router";
import store from "@/store/store";

// Declare the emits option
const emit = defineEmits(['userCreated']);

const formData = ref({
  username: '',
  password: '',
  check_password: '',
  first_name: '',
  last_name: '',
  gender: '',
  email: '',
  phone_number: '',
  is_fitter: false,
  is_manager: false,
  is_engineer: false
});

const clearForm = () => {
  formData.value = {
    username: '',
    password: '',
    check_password: '',
    first_name: '',
    last_name: '',
    gender: '',
    email: '',
    phone_number: '',
    is_fitter: false,
    is_manager: false,
    is_engineer: false
  };
};

const createNewAccount = async () => {
  const auth_token = store.getters['tokens/access'];
  const refresh_token = store.getters['tokens/refresh'];
  const tokenStatus = await checkTokens(auth_token, refresh_token, store);

  if (tokenStatus.redirect) {
    await router.push(tokenStatus.redirect); // Redirect to log in if needed
  }

  if (!checkRegexPassword(formData.value.password)) {
    alert('Пароль должен содержать как минимум 8 символов, включая заглавные и строчные буквы, цифры и специальные символы.');
    return;
  }

  if (formData.value.password !== formData.value.check_password) {
    alert('Пароли не совпадают!');
    return;
  }

  try {
    await axios.post('account/', {
      "user": {
        "username": formData.value.username,
        "password": formData.value.password,
        "email": formData.value.email,
        "first_name": formData.value.first_name,
        "last_name": formData.value.last_name
      },
      "phone_number": formData.value.phone_number,
      "is_fitter": formData.value.is_fitter,
      "is_manager": formData.value.is_manager,
      "is_engineer": formData.value.is_engineer,
      "gender": formData.value.gender
    });

    clearForm();
    emit('userCreated'); // Emit the userCreated event
  } catch (error) {
    if (error.response) {
      const errors = error.response.data;

      if (errors.user && errors.user.username) {
        alert(`Ошибка: ${errors.user.username[0]}`);
      } else {
        alert('Произошла неизвестная ошибка.');
      }

      console.error('Ошибка:', errors);
    } else if (error.request) {
      alert('Нет ответа от сервера. Пожалуйста, попробуйте позже.');
    } else {
      alert(`Непредвиденная ошибка: ${error.message}`);
    }
  }
};

const checkRegexPassword = (str) => {
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@.#$!%*?&=_;])[A-Za-z\d@.#$!%*?&=_;]{8,}$/;
  return regex.test(str);
};

const passwordMismatch = computed(() => {
  return (
      formData.value.password !== '' &&
      formData.value.check_password !== '' &&
      formData.value.password !== formData.value.check_password
  );
});
</script>

<template>
  <button
      class="btn btn-primary my-2"
      data-bs-toggle="modal"
      href="#new_user"
      role="button"
  >Новый пользователь</button>
  <div
      class="modal fade"
      id="new_user"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      aria-labelledby="new_user"
      tabindex="-1"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title text-bold">Новый пользователь</h4>
          <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              @click="clearForm"
          ></button>
        </div>
        <div class="modal-body">
          <div class="mx-2">
            <input
                type="text"
                class="form-control mt-3"
                placeholder="Имя пользователя"
                aria-label="username"
                v-model="formData.username"
                required
            >
            <input
                type="password"
                class="form-control mt-3"
                placeholder="Пароль"
                aria-label="password"
                v-model="formData.password"
                required
            >
            <input
                type="password"
                class="form-control mt-3"
                placeholder="Повторите пароль"
                aria-label="password-check"
                v-model="formData.check_password"
                required
            >
            <input
                type="text"
                class="form-control mt-3"
                placeholder="Имя"
                aria-label="first_name"
                v-model="formData.first_name"
                required
            >
            <input
                type="text"
                class="form-control mt-3"
                placeholder="Фамилия"
                aria-label="last_name"
                v-model="formData.last_name"
                required
            >
            <input
                type="text"
                class="form-control mt-3"
                placeholder="Номер телефона"
                aria-label="phone_number"
                v-model="formData.phone_number"
                required
            >
            <input
                type="email"
                class="form-control mt-3"
                placeholder="Электронная почта"
                aria-label="email"
                v-model="formData.email"
                required
            >
            <p class="text text-bold">Пол</p>
            <select class="form-select" aria-label="gender" v-model="formData.gender">
              <option value="MALE">Мужской</option>
              <option value="FEMALE">Женский</option>
            </select>
            <p class="text text-bold">Роль</p>
            <div class="form-check">
              <input class="form-check-input" type="radio" id="is_engineer_input" name="user_role" :value="true" v-model="formData.is_engineer">
              <label class="form-check-label" for="is_engineer_input">Инженер</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" id="is_manager_input" name="user_role" :value="true" v-model="formData.is_manager">
              <label class="form-check-label" for="is_manager_input">Менеджер</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" id="is_fitter_input" name="user_role" :value="true" v-model="formData.is_fitter">
              <label class="form-check-label" for="is_fitter_input">Монтёр</label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <p
              v-if="formData.check_password && passwordMismatch"
              class="text-danger"
          >
            Пароли не совпадают!
          </p>
          <button
              class="btn btn-primary"
              data-bs-dismiss="modal"
              :disabled="passwordMismatch"
              @click="createNewAccount"
          >Создать</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>